import useSWR from 'swr';

import { ENDPOINTS } from '@/api/endpoints';
import { getCaseById, type Case } from '@/api/cases';
import { type StandardResponse } from '@/api/standard-response';

type GetCaseByIdProps = {
  caseId?: string;
};

/**
 * Hook to fetch a single case by ID with full case metadata
 * @param caseId - The case ID to fetch (optional, hook won't fetch if undefined)
 * @returns Object with loading state, error, case data, and refetch function
 */
export const useGetCaseById = ({ caseId }: GetCaseByIdProps) => {
  // Only create SWR key if caseId is provided
  const key = caseId ? [ENDPOINTS.CASE, caseId, 'grading'] : null;

  const { data, error, isLoading, mutate } = useSWR<StandardResponse<Case>, Error>(
    key,
    () => getCaseById(caseId!), // caseId is guaranteed to exist when key is not null
    {
      errorRetryCount: 1,
      revalidateOnFocus: false, // Case data is relatively static
    }
  );

  return {
    loading: isLoading,
    error,
    case: data?.data, // Extract case data from StandardResponse wrapper
    refetch: mutate,
  };
};
